// /src/pages/private/members/personalhub/index.jsx

import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PersonalHubSideRail from './PersonalHubSideRail';
import { PersonalHubNavigationProvider } from './PersonalHubNavigationContext';
import PersonalHubDashboardSection from './PersonalHubDashboardSection';
import {
  PERSONAL_HUB_SECTION_BLUEPRINTS,
  PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS,
  PERSONAL_HUB_SECTION_DASHBOARD,
  PERSONAL_HUB_SECTION_HANGAR,
  PERSONAL_HUB_SECTION_IDS,
  personalHubSectionPath,
  resolvePersonalHubSection,
} from './personalHubRailConfig';
import {
  loadBlueprintSection,
  loadFactionReputationSection,
  loadHangarSection,
  preloadPersonalHubSection,
  preloadPersonalHubSections,
} from './tools/personalHubSectionPreload';
import usePersonalHubHandleId from './hooks/usePersonalHubHandleId';
import { useSecurity } from '../../../../SecurityContext';

const Hangar = lazy(loadHangarSection);
const FactionReputation = lazy(loadFactionReputationSection);
const Blueprint = lazy(loadBlueprintSection);

function scheduleIdleTask(task) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(task, { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }

  const id = window.setTimeout(task, 400);
  return () => window.clearTimeout(id);
}

export default function PersonalHub() {
  const { section: sectionParam } = useParams();
  const navigate = useNavigate();
  const {
    subjectHasPermission,
    isViewAsActive,
    viewAsLoading,
    permissionsVersion,
  } = useSecurity();
  const activeSection = resolvePersonalHubSection(sectionParam);
  const [mountedSections, setMountedSections] = useState(
    () => new Set([resolvePersonalHubSection(sectionParam)])
  );
  const { handleId } = usePersonalHubHandleId();

  const canOpenHangarSection = subjectHasPermission('private.navbar.personalhub.hangar', 'read');
  const canOpenBlueprintsSection = subjectHasPermission(
    'private.navbar.personalhub.blueprints',
    'read'
  );
  const canOpenFactionsReputationsSection = subjectHasPermission(
    'private.navbar.personalhub.factionsReputations',
    'read'
  );
  const permissionsPending = isViewAsActive && viewAsLoading;
  const hubShellRef = useRef(null);

  const mountSection = useCallback((section) => {
    setMountedSections((prev) => {
      if (prev.has(section)) return prev;
      const next = new Set(prev);
      next.add(section);
      return next;
    });
  }, []);

  const selectSection = useCallback(
    (section) => {
      preloadPersonalHubSection(section);
      mountSection(section);
      navigate(personalHubSectionPath(section));
    },
    [mountSection, navigate]
  );

  useEffect(() => {
    mountSection(activeSection);
    preloadPersonalHubSection(activeSection);
  }, [activeSection, mountSection]);

  useEffect(() => {
    if (!sectionParam || PERSONAL_HUB_SECTION_IDS.includes(sectionParam)) return;
    navigate(personalHubSectionPath(PERSONAL_HUB_SECTION_DASHBOARD), { replace: true });
  }, [sectionParam, navigate]);

  useEffect(() => {
    if (permissionsPending) return;

    if (activeSection === PERSONAL_HUB_SECTION_HANGAR && !canOpenHangarSection) {
      navigate(personalHubSectionPath(PERSONAL_HUB_SECTION_DASHBOARD), { replace: true });
    }
    if (activeSection === PERSONAL_HUB_SECTION_BLUEPRINTS && !canOpenBlueprintsSection) {
      navigate(personalHubSectionPath(PERSONAL_HUB_SECTION_DASHBOARD), { replace: true });
    }
    if (
      activeSection === PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS &&
      !canOpenFactionsReputationsSection
    ) {
      navigate(personalHubSectionPath(PERSONAL_HUB_SECTION_DASHBOARD), { replace: true });
    }
  }, [
    activeSection,
    canOpenHangarSection,
    canOpenBlueprintsSection,
    canOpenFactionsReputationsSection,
    permissionsPending,
    permissionsVersion,
    navigate,
  ]);

  useEffect(() => {
    const sections = [];
    if (canOpenHangarSection) sections.push(PERSONAL_HUB_SECTION_HANGAR);
    if (canOpenFactionsReputationsSection) {
      sections.push(PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS);
    }
    if (canOpenBlueprintsSection) sections.push(PERSONAL_HUB_SECTION_BLUEPRINTS);
    if (!sections.length) return undefined;

    return scheduleIdleTask(() => {
      preloadPersonalHubSections(sections);
    });
  }, [
    canOpenHangarSection,
    canOpenBlueprintsSection,
    canOpenFactionsReputationsSection,
  ]);

  useEffect(() => {
    if (activeSection !== PERSONAL_HUB_SECTION_HANGAR || !canOpenHangarSection) {
      return undefined;
    }

    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [activeSection, canOpenHangarSection]);

  useEffect(() => {
    if (activeSection !== PERSONAL_HUB_SECTION_HANGAR || !canOpenHangarSection) {
      return undefined;
    }

    const shell = hubShellRef.current;
    if (!shell) return undefined;

    const onWheel = (e) => {
      if (e.target.closest('.hangar-page')) return;

      const content = shell.querySelector('.hangar-content');
      if (!content) return;

      const maxScroll = Math.max(0, content.scrollHeight - content.clientHeight);
      const next = Math.min(maxScroll, Math.max(0, content.scrollTop + e.deltaY));

      if (content.contains(e.target) && next === content.scrollTop + e.deltaY) {
        return;
      }

      e.preventDefault();
      content.scrollTop = next;
    };

    shell.addEventListener('wheel', onWheel, { passive: false });
    return () => shell.removeEventListener('wheel', onWheel);
  }, [activeSection, canOpenHangarSection]);

  const showHangar = canOpenHangarSection;
  const showBlueprints = canOpenBlueprintsSection;
  const showFactionsReputations = canOpenFactionsReputationsSection;

  const isSectionMounted = (section) => mountedSections.has(section);

  return (
    <PersonalHubNavigationProvider
      onSelectSection={selectSection}
      canOpenFactionsReputations={showFactionsReputations}
    >
      <div className="private-pages private-pages--personal-hub">
        <div
          ref={hubShellRef}
          className="member-dashboard member-dashboard--with-rail"
          data-resource="private.navbar.personalhub"
          data-action="read"
          data-description="Navbar → Personal hub"
        >
          <PersonalHubSideRail
            activeSection={activeSection}
            onSelectSection={selectSection}
            onPrefetchSection={preloadPersonalHubSection}
          />

          <div
            className={[
              'member-dashboard__panel',
              activeSection === PERSONAL_HUB_SECTION_HANGAR
                ? 'member-dashboard__panel--hangar'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {isSectionMounted(PERSONAL_HUB_SECTION_DASHBOARD) ? (
              <PersonalHubDashboardSection
                isActive={activeSection === PERSONAL_HUB_SECTION_DASHBOARD}
              />
            ) : null}

            {isSectionMounted(PERSONAL_HUB_SECTION_HANGAR) && showHangar ? (
              <div
                className={[
                  'personal-hub-section',
                  activeSection !== PERSONAL_HUB_SECTION_HANGAR
                    ? 'personal-hub-section--inactive'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Suspense fallback={null}>
                  <Hangar embeddedInPersonalHub />
                </Suspense>
              </div>
            ) : null}

            {isSectionMounted(PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS) &&
            showFactionsReputations ? (
              <div
                className={[
                  'personal-hub-section',
                  activeSection !== PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS
                    ? 'personal-hub-section--inactive'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Suspense fallback={null}>
                  <FactionReputation handleId={handleId} />
                </Suspense>
              </div>
            ) : null}

            {isSectionMounted(PERSONAL_HUB_SECTION_BLUEPRINTS) && showBlueprints ? (
              <div
                className={[
                  'personal-hub-section',
                  activeSection !== PERSONAL_HUB_SECTION_BLUEPRINTS
                    ? 'personal-hub-section--inactive'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Suspense fallback={null}>
                  <Blueprint handleId={handleId} />
                </Suspense>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </PersonalHubNavigationProvider>
  );
}
